import { chakra, Flex, Icon, Text } from "@chakra-ui/react";
import {
  decimalToPercentage,
  isDefined,
  toNumberDisplayValue,
} from "@intentsify/utils";
import { Badge } from "components";
import { LegacyCard as Card } from "../../Card/legacy/Card";
import type { StatCardProps } from "../StatCard";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { getArrowType, getColorScheme } from "./utils";

/** The pre-restyle stat card. */
const LegacyStatCard = <
  T extends number | undefined | null = number | undefined | null
>({
  value,
  title,
  description,
  caption,
  valueFormatter = toNumberDisplayValue,
  previousPeriodChange,
  previousPeriodLabel = "vs Previous period:",
  titleTooltip,
  titleTooltipPlacement,
  colorScheme = "white",
  isLoading = false,
  hasData = true,
  noDataMessage,
  noDataIcon,
  noValueMessage = "n/a",
}: StatCardProps<T>) => {
  const arrowType = getArrowType(previousPeriodChange);

  return (
    <Card
      title={title}
      titleTooltip={titleTooltip}
      titleTooltipPlacement={titleTooltipPlacement}
      isLoading={isLoading}
      colorScheme={colorScheme}
      hasData={hasData}
      noDataMessage={noDataMessage}
      noDataIcon={noDataIcon}
    >
      <Flex
        flexGrow={1}
        alignItems="center"
        justifyContent="center"
        gap="0.1rem"
        flexDir="column"
      >
        <Flex flexGrow={1} alignItems="center" justifyContent="center">
          <Text
            textAlign="center"
            fontSize="3xl"
            fontWeight="semibold"
            lineHeight="1.25"
          >
            {value || value === 0 ? valueFormatter(value) : noValueMessage}
          </Text>
        </Flex>

        <Flex flexDir="column" alignItems="center" justifyContent="center">
          {description && (
            <Text textAlign="center" fontSize="sm" fontWeight="semibold">
              {description}
            </Text>
          )}

          {caption && (
            <Text
              textAlign="center"
              fontSize="sm"
              fontWeight="semibold"
              color="blackAlpha.500"
              _dark={{
                color: "whiteAlpha.600",
              }}
            >
              {caption}
            </Text>
          )}
        </Flex>
      </Flex>

      <Flex height={5} justifyContent="center">
        <Text as="div" fontSize="11px" fontWeight="normal">
          {isDefined(previousPeriodChange) && (
            <Flex alignItems={"center"}>
              {previousPeriodLabel}
              <Badge ml={1} colorScheme={getColorScheme(arrowType)}>
                <Flex alignItems="center">
                  {!arrowType && (
                    <>
                      {" "}
                      0%
                      <chakra.span opacity={0.5}>-</chakra.span>
                    </>
                  )}
                  {arrowType && (
                    <>
                      {" "}
                      {toNumberDisplayValue(
                        decimalToPercentage(previousPeriodChange ?? 0)
                      )}
                      %
                      {arrowType === "increase" ? (
                        <Icon as={FiChevronUp} w={4} h={4} />
                      ) : (
                        <Icon as={FiChevronDown} w={4} h={4} />
                      )}
                    </>
                  )}
                </Flex>
              </Badge>
            </Flex>
          )}
        </Text>
      </Flex>
    </Card>
  );
};

export { LegacyStatCard };
